import { UNTITLED_CAPTURE_PLACEHOLDER, type TicketSummary } from '@vd/types';

/**
 * Shared display-title helper for any UI surface that renders a ticket's
 * title at-a-glance (cards, table rows, search results, board lists).
 *
 * While the AI title-generation worker is in flight, the ticket's stored
 * title is the literal placeholder "Untitled capture" that capture upload
 * sets at create time. Showing that on every card looks broken, especially
 * because the user is staring at the board waiting for AI to finish.
 * Substitute one of these hint strings (deterministically picked per
 * ticket id so the same card keeps the same hint between renders) until
 * `titlePending` flips false on the server.
 *
 * The TicketDetail page renders a richer animated overlay with pulsing
 * dots and uses its own copy variant. Cards / table cells use this
 * helper because the constrained space doesn't have room for the dots
 * and the italic + pulse styling at the rendering site is enough.
 */
export const TITLE_PENDING_HINTS: readonly string[] = [
  "Toado's drafting a title…",
  'Catching this one for you…',
  'Letting the toad name this bug…',
  "Toad's chewing on a title…",
  'Hopping into a title…',
  "Bug spotted, toad's on it…",
  'Croaking out a title…',
];

function hashTicketId(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function isTitlePending(ticket: Pick<TicketSummary, 'title' | 'titlePending'>): boolean {
  return !!ticket.titlePending && ticket.title === UNTITLED_CAPTURE_PLACEHOLDER;
}

export function displayTicketTitle(ticket: Pick<TicketSummary, 'id' | 'title' | 'titlePending'>): string {
  if (isTitlePending(ticket)) {
    return TITLE_PENDING_HINTS[hashTicketId(ticket.id) % TITLE_PENDING_HINTS.length]!;
  }
  return ticket.title;
}
